import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { AuthContext } from '@/contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import styles from './Admin.module.css';
import { useTranslation } from 'react-i18next';
import {
	CarDTO,
	CarImageDTO,
	CustomAdminPageResourceService,
	CustomCarImageResourceService,
	CustomCarResourceService,
	CustomFaqResourceService,
	CustomSignallerResourceService,
	FAQDTO,
	SignallerDTO,
	StatsDTO,
	UserDTO,
	UserSearchStatusDTO
} from '@/openapi';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import Collapse from '@/components/UI/Collapse';
import CarForm from '@/components/CarForm';
import { ImageSlot } from '@/components/ImageUploader/ImageUploader';
import { useRemotePost } from '@/hooks/useRemotePost';
import Loader from '@/components/UI/Loader/Loader';
import Label from '@/components/UI/Label';
import CarCard from '@/components/CarCard';
import Pagination from '@/components/UI/Paging';
import { usePaginatedApi } from '@/hooks/usePaginatedApi';
import { imageSlotToDTO } from '@/utils/utils';
import Faq from '@/components/FAQArea/Faq';
import Button from '@/components/UI/Button';
import { useRemoteDelete } from '@/hooks/useRemoteDelete';
import { useRemoteUpdate } from '@/hooks/useRemoteUpdate';
import SignallerList from '@/components/SignallerList';
import SuccessToast from '@/components/UI/SuccessToast';
import Modal from '@/components/UI/Modal';
import InactiveUsersList from '@/components/InactiveUsersList';

const Admin = () => {
	const ADMIN_CARS_CONTEXT_ID = 'admin-cars';
	const SIGNALLERS_TO_PAY_CONTEXT_ID = 'admin-signallers-to-pay';
	const SIGNALLERS_TO_REWARD_CONTEXT_ID = 'admin-signallers-to-reward';
	const ADMIN_INACTIVE_USERS = 'inactive-users';
	const ADMIN_INACTIVE_SIGNALLERS = 'inactive-signallers';
	const ADMIN_ACTIVE_SIGNALLERS = 'active-signallers';
	const SEARCH_USERS_CONTEXT_ID = 'admin-search-users';

	const { t } = useTranslation();

	const authCtx = useContext(AuthContext);
	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');

	const [successToast, setSuccessToast] = useState<{
		isVisible: boolean;
		message: string;
		type: 'success' | 'error';
	}>({
		isVisible: false,
		message: '',
		type: 'success'
	});

	const [carCatalogCategory, setCarCatalogCategory] = useState<'RENTAL' | 'USED' /* | 'VARIOUS'*/>('RENTAL');
	const [showInactiveUsersModal, setShowInactiveUsersModal] = useState<boolean>(false);
	const [showInactiveSignallersModal, setShowInactiveSignallersModal] = useState<boolean>(false);
	const [showActiveSignallersModal, setShowActiveSignallersModal] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>('');

	// ------------------------------------- CHIAMATE API -------------------------------------

	const getStats = useRemoteGet<StatsDTO>({
		remoteMethod: async () => {
			return CustomAdminPageResourceService.getStats();
		},
		lazy: true
	});

	const getInactiveUsers = usePaginatedApi<UserDTO>(
		async (pagingOptions) => {
			if (!isRoleAdmin) {
				// Ritorna un oggetto vuoto
				return { content: [], totalElements: 0, totalPages: 0 };
			}

			return CustomAdminPageResourceService.getUsersWithoutSignaller({
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'login',
			sortDir: 'asc'
		},
		ADMIN_INACTIVE_USERS
	);

	const getInactiveSignallers = usePaginatedApi<UserDTO>(
		async (pagingOptions) => {
			if (!isRoleAdmin) {
				// Ritorna un oggetto vuoto
				return { content: [], totalElements: 0, totalPages: 0 };
			}

			return CustomAdminPageResourceService.getInactiveSignallers({
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'dateCreate',
			sortDir: 'asc'
		},
		ADMIN_INACTIVE_SIGNALLERS
	);

	const getActiveSignallers = usePaginatedApi<UserDTO>(
		async (pagingOptions) => {
			if (!isRoleAdmin) {
				// Ritorna un oggetto vuoto
				return { content: [], totalElements: 0, totalPages: 0 };
			}

			return CustomAdminPageResourceService.getActiveSignallers({
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'dateCreate',
			sortDir: 'asc'
		},
		ADMIN_ACTIVE_SIGNALLERS
	);

	const searchUsers = usePaginatedApi<UserSearchStatusDTO>(
		async (pagingOptions) => {
			if (!isRoleAdmin || !searchTerm || searchTerm.length < 1) {
				return { content: [], totalElements: 0, totalPages: 0 };
			}

			return CustomAdminPageResourceService.searchUsers({
				email: searchTerm,
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'email',
			sortDir: 'asc'
		},
		SEARCH_USERS_CONTEXT_ID
	);

	const signallersToBePaid = usePaginatedApi<SignallerDTO>(
		async (pagingOptions) => {
			return CustomSignallerResourceService.getAllSignallersToBePaid({
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'dateCreate',
			sortDir: 'asc'
		},
		SIGNALLERS_TO_PAY_CONTEXT_ID
	);

	const signallersToBeRewarded = usePaginatedApi<SignallerDTO>(
		async (pagingOptions) => {
			return CustomSignallerResourceService.getAllSignallersToBeRewarded({
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 10,
			sortBy: 'dateCreate',
			sortDir: 'asc'
		},
		SIGNALLERS_TO_REWARD_CONTEXT_ID
	);

	const createCar = useRemotePost<CarDTO, { requestBody: CarDTO }>({
		remoteMethod: CustomCarResourceService.createCarOffer,
		lazy: true
	});

	const carCatalog = usePaginatedApi<CarDTO>(
		async (pagingOptions) => {
			return CustomCarResourceService.getAdvancedFilteredOffers({
				category: carCatalogCategory,
				maxPrice: undefined,
				brand: undefined,
				doors: undefined,
				transmission: undefined,
				fuelType: undefined,
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 9,
			sortBy: 'brand',
			sortDir: 'asc'
		},
		ADMIN_CARS_CONTEXT_ID
	);

	const getCar = useRemoteGet<CarDTO>({
		remoteMethod: async () => {
			if (!deletingCarId && !editingCarId) {
				throw new Error('No Car ID');
			}

			const carId = isDeletingCar ? deletingCarId : isEditingCar ? editingCarId : null;
			return CustomCarResourceService.getOffer({ carId: carId! });
		},
		lazy: true
	});

	const updateCar = useRemoteUpdate<CarDTO, CarDTO>({
		remoteMethod: async (carId, payload) => {
			return CustomCarResourceService.partialUpdateOffer({
				carId: parseInt(carId.toString()),
				requestBody: payload
			});
		},
		lazy: true
	});

	const deleteCar = useRemoteDelete<void>({
		remoteMethod: async (carId) => {
			return CustomCarResourceService.deleteCarOffer({
				id: parseInt(carId.toString())
			});
		},
		lazy: true
	});

	const createCarImage = useRemotePost<CarImageDTO, { carId: number; requestBody: CarImageDTO }>({
		remoteMethod: CustomCarImageResourceService.createCarImage,
		lazy: true
	});

	const deleteCarImage = useRemoteDelete<void>({
		remoteMethod: async (carImageId) => {
			return CustomCarImageResourceService.deleteImage({
				id: parseInt(carImageId.toString())
			});
		},
		lazy: true
	});

	const createFaq = useRemotePost<FAQDTO, { requestBody: FAQDTO }>({
		remoteMethod: async (payload) => {
			return CustomFaqResourceService.createFaq1(payload);
		}
	});

	const getAllFaqs = useRemoteGet<FAQDTO[]>({
		remoteMethod: async () => {
			return CustomFaqResourceService.getAllFaqFirstLevelByCategory({ category });
		},
		lazy: true
	});

	const updateFaq = useRemoteUpdate<FAQDTO, FAQDTO>({
		remoteMethod: async (faqId, payload) => {
			return CustomFaqResourceService.partialUpdateFaq({
				faqId: parseInt(faqId.toString()),
				requestBody: payload
			});
		},
		lazy: true
	});

	const deleteFaq = useRemoteDelete<void>({
		remoteMethod: async (faqId) => {
			return CustomFaqResourceService.deleteFaq1({
				id: parseInt(faqId.toString())
			});
		},
		lazy: true
	});

	// ------------------------------------- STATISTICS SECTION -------------------------------------

	useEffect(() => {
		if (isRoleAdmin) {
			getStats.fetch();
			getInactiveUsers.fetch();
			getInactiveSignallers.fetch();
			getActiveSignallers.fetch();
		}
	}, []);

	// ------------------------------------- USER SEARCH SECTION -------------------------------------

	// trigger search on input change
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchTerm.length >= 1) {
				searchUsers.fetch();
			} else {
				// Manual reset if necessary
			}
		}, 1000); // Debounce

		return () => clearTimeout(delayDebounceFn);
	}, [searchTerm]);

	// Helper to assign user -> message of his status
	const getStatusDetails = (statusType: string | undefined) => {
		switch (statusType) {
			case 'NO_SIGNALLER':
				return {
					text: t('adminPage.search.noSignaller'),
					className: styles.status_no_signaller
				};
			case 'INACTIVE_SIGNALLER':
				return {
					text: t('adminPage.search.inactive'),
					className: styles.status_inactive
				};
			case 'ACTIVE_SIGNALLER':
				return {
					text: t('adminPage.search.active'),
					className: styles.status_active
				};
			default:
				return { text: t('adminPage.search.unknown'), className: '' };
		}
	};

	// ------------------------------------- CREATE CAR SECTION -------------------------------------

	const [editingCarId, setEditingCarId] = useState<number | null>(null);
	const [deletingCarId, setDeletingCarId] = useState<number | null>(null);
	const [isEditingCar, setIsEditingCar] = useState<boolean>(false);
	const [isDeletingCar, setIsDeletingCar] = useState<boolean>(false);
	const [existingCarImages, setExistingCarImages] = useState<ImageSlot[]>([]);
	const [originalImageIds, setOriginalImageIds] = useState<number[]>([]);
	const [pendingImages, setPendingImages] = useState<ImageSlot[]>([]);

	// Funzione per resettare tutti gli stati
	const resetAllStates = () => {
		setEditingCarId(null);
		setDeletingCarId(null);
		setIsEditingCar(false);
		setIsDeletingCar(false);
		setExistingCarImages([]);
		setOriginalImageIds([]);
		setPendingImages([]);

		setTimeout(() => {
			carCatalog.fetch();
		}, 500);
	};

	// Funzione per mostrare il toast
	const showToast = (message: string, isError: boolean = false) => {
		setSuccessToast({
			isVisible: true,
			message,
			type: isError ? 'error' : 'success'
		});
	};

	// CREATE: funzione per creare una car
	const carSubmitHandler = async (car: CarDTO, images: ImageSlot[]) => {
		if (isRoleAdmin) {
			try {
				setPendingImages(images.filter((img) => !img.isExisting && img.file));
				await createCar.fetch({ requestBody: car });

				showToast(t('adminPage.offersSection.carCreated'));
			} catch (err: any) {
				console.error(err);
				showToast(t('adminPage.offersSection.errorSavingCar') || err.message, true);
				setPendingImages([]); // Reset in caso di errore
			}
		}
	};

	// CREATE: funzione per creare le immagini in sequenza
	useEffect(() => {
		const carId = createCar.data?.id;
		if (!carId || pendingImages.length === 0) return;

		(async () => {
			try {
				for (const slot of pendingImages) {
					const dto = await imageSlotToDTO(slot);
					await createCarImage.fetch({ carId, requestBody: dto });
				}

				setPendingImages([]);
				resetAllStates();
			} catch (err: any) {
				console.error(err);
				showToast(t('adminPage.offersSection.errorCreatingImages'), true);
			} finally {
				setPendingImages([]);
			}
		})();
	}, [createCar.data]);

	// -------------------------------------- UPDATE CAR SECTION -------------------------------------

	// UPDATE
	const handleCarSubmit = async (car: CarDTO, images: ImageSlot[]) => {
		if (isEditingCar && editingCarId) {
			try {
				// 1. Trova gli ID delle immagini attuali
				const currImgs = images.filter((img) => img.isExisting && img.imageId).map((img) => img.imageId as number);

				// 2. Elimina in sequenza le immagini rimosse (rispetto alle originali)
				const imgToDelete = originalImageIds.filter((id) => !currImgs.includes(id));
				if (imgToDelete.length > 0) {
					for (const imageId of imgToDelete) {
						await deleteCarImage.fetch(imageId);
					}
				}

				// 3. Crea in sequenza le immagini aggiunte (rispetto alle esistenti)
				const newImages = images.filter((img) => !img.isExisting && img.file);
				if (newImages.length > 0) {
					for (const slot of newImages) {
						const dto = await imageSlotToDTO(slot);
						await createCarImage.fetch({ carId: editingCarId, requestBody: dto });
					}
				}

				// 4. Aggiorna la car esistente
				const updatedCar = {
					...car,
					id: editingCarId
					// Il servizio backend di updateCar non gestisce direttamente l'aggiornamento delle carImages
				};
				delete (updatedCar as any).imageIds; // evita di inviare imageIds obsolete
				await updateCar.fetch(editingCarId, updatedCar);
				showToast(t('adminPage.offersSection.carUpdated'));

				// Aggiornamento
				resetAllStates();
			} catch (err: any) {
				console.error(err);
				showToast(t('adminPage.offersSection.errorUpdatingCar') || err.message, true);
			}
		} else {
			// Se non è in modifica, Creazione nuova auto
			carSubmitHandler(car, images);
		}
	};

	// UPDATE: Funzione per caricare i dati della Car nel form
	const handleEditCar = async (car: CarDTO) => {
		if (car.id) {
			resetAllStates();

			// Inizio processo di modifica
			setIsEditingCar(true);
			setEditingCarId(car.id);

			// Scorre fino al form
			const formElement = document.getElementById('carForm');
			if (formElement) {
				formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}
	};

	// UPDATE: Funzione per caricare le immagini
	const loadCarImages = async (imageIds: number[]) => {
		if (!imageIds?.length) {
			setExistingCarImages([]);
			setOriginalImageIds([]);
			return;
		}

		try {
			// 1) parallel fetch, ogni callback restituisce ImageSlot | null
			const fetches = imageIds.map(async (id): Promise<ImageSlot | null> => {
				try {
					const response = await CustomCarImageResourceService.getImage({
						imageId: id
					});
					if (response?.src) {
						return {
							previewUrl: `data:${response.srcContentType};base64,${response.src}`,
							isExisting: true,
							imageId: id
						};
					}
					return null;
				} catch {
					return null;
				}
			});

			// 2) attende tutti e filtra via i null con type-guard
			const results = await Promise.all(fetches);
			const loadedImages: ImageSlot[] = results.filter((img): img is ImageSlot => img !== null);

			// 3) aggiorna stato
			setExistingCarImages(loadedImages);
			setOriginalImageIds(loadedImages.map((img) => img.imageId!));
		} catch (err) {
			console.error('Errore nel caricamento delle immagini:', err);
			showToast(t('adminPage.offersSection.errorLoadingImages'), true);
		}
	};

	// DELETE: Funzione per disabilitare un'offerta
	const handleDisableCar = async (carId: number) => {
		// Inizio processo eliminazione
		setIsDeletingCar(true);
		setDeletingCarId(carId);

		// Reset degli stati prima dell'eliminazione
		setIsEditingCar(false);
		setEditingCarId(null);
		setExistingCarImages([]);
		setOriginalImageIds([]);
		setPendingImages([]);
	};

	// UPDATE e DELETE: Al caricamento dei dati della car, gestisce la modifica o la cancellazione
	useEffect(() => {
		if (!getCar.data || !isRoleAdmin) return;
		const data = getCar.data;

		// AGGIORNAMENTO
		// Se ho i dati dell'auto e le immagini sono presenti, carico le immagini nel form
		if (isEditingCar && editingCarId && editingCarId === data.id) {
			if (data.imageIds && data.imageIds.length > 0) {
				loadCarImages(data.imageIds);
			} else {
				setExistingCarImages([]);
				setOriginalImageIds([]);
			}
		}

		// ELIMINAZIONE
		// Elimina le immagini associate e la car
		else if (isDeletingCar && deletingCarId && deletingCarId === data.id) {
			(async () => {
				try {
					if (data.imageIds && data.imageIds.length > 0) {
						await Promise.all(data.imageIds.map((imageId) => deleteCarImage.fetch(imageId)));
					}

					await deleteCar.fetch(deletingCarId);
					showToast(t('adminPage.offersSection.carDeleted'));
				} catch (err: any) {
					console.error(err);
					showToast(t('adminPage.offersSection.errorDeletingCar'), true);
				} finally {
					resetAllStates();
				}
			})();
		}
	}, [getCar.data]);

	// UPDATE e DELETE: Funzione per scatenare la get dei dettagli della Car
	useEffect(() => {
		if ((editingCarId && isEditingCar && !isDeletingCar) || (deletingCarId && isDeletingCar && !isEditingCar)) {
			try {
				getCar.fetch();
			} catch (err: any) {
				console.error(err);
				showToast(t('adminPage.offersSection.errorGettingCar') || err.message, true);
				resetAllStates();
			}
		}
	}, [editingCarId, isEditingCar, deletingCarId, isDeletingCar]);

	// Aggiornamento lista offerte
	useEffect(() => {
		if (isRoleAdmin) {
			getStats.fetch();
			carCatalog.fetch();
			signallersToBePaid.fetch();
			signallersToBeRewarded.fetch();
		}
	}, [isRoleAdmin, deleteCar.success]);

	useEffect(() => {
		carCatalog.fetch();
	}, [carCatalogCategory]);

	// ------------------------------------- CREATE FAQ SECTION -------------------------------------

	const [createCategory, setCreateCategory] = useState<string>('GENERAL_CONDITIONS');
	const [createQuestion, setCreateQuestion] = useState<string>('');
	const [createAnswer, setCreateAnswer] = useState<string>('');

	useEffect(() => {
		if (typeof createFaq.error !== 'undefined') {
			showToast(t('common.tokenExpired'), true);
		}
	}, [createFaq.error]);

	const createFaqSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!createQuestion.trim() || !createAnswer.trim()) {
			showToast(t('adminPage.faqSection.error'), true);
			return;
		}

		const newFaq: FAQDTO = {
			category: createCategory as FAQDTO['category'],
			question: createQuestion,
			answer: createAnswer
		};

		if (newFaq.question?.trim() && newFaq.answer?.trim()) await createFaq.fetch({ requestBody: newFaq });
	};

	const resetFaqForm = () => {
		setCreateCategory('GENERAL_CONDITIONS');
		setCreateQuestion('');
		setCreateAnswer('');
		setEditingFaqId(null);
		setIsEditingFaq(false);
	};

	// ------------------------------------- UPDATE FAQ SECTION -------------------------------------

	const [category, setCategory] = useState('MINIMUM_CUSTOMER_REQUIREMENTS');
	const [editingFaqId, setEditingFaqId] = useState<number | null>(null);
	const [isEditingFaq, setIsEditingFaq] = useState<boolean>(false);

	const changeCategory = (newCategory: string) => {
		setCategory(newCategory);
	};

	const handleDeleteFaq = async (faqId: number) => {
		if (!faqId) return;
		await deleteFaq.fetch(faqId);
	};

	const handleEditFaq = (faq: FAQDTO) => {
		if (faq.id !== undefined) {
			setEditingFaqId(faq.id);
			setIsEditingFaq(true);
			setCreateCategory(faq.category || 'GENERAL_CONDITIONS');
			setCreateQuestion(faq.question || '');
			setCreateAnswer(faq.answer || '');

			// Scorri fino al form
			const formElement = document.getElementById('updateFaqForm');
			if (formElement) {
				formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}
	};

	const updateFaqSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!createQuestion.trim() || !createAnswer.trim()) {
			showToast(t('adminPage.faqSection.error'), true);
			return;
		}

		if (editingFaqId) {
			const updatedFaq: FAQDTO = {
				category: createCategory as FAQDTO['category'],
				question: createQuestion,
				answer: createAnswer,
				id: editingFaqId
			};

			await updateFaq.fetch(editingFaqId, updatedFaq);
		}
	};

	// Aggiornamento della lista dopo le operazioni di aggiornamento
	useEffect(() => {
		getAllFaqs.fetch();
		getStats.fetch();

		if (deleteFaq.success) {
			showToast(t('adminPage.faqSection.faqDeleted'));
			deleteFaq.resetState();
		}

		if (createFaq.data) {
			showToast(t('adminPage.faqSection.faqCreated'));
			resetFaqForm();
			createFaq.resetState();
		}

		if (updateFaq.data) {
			showToast(t('adminPage.faqSection.faqUpdated'));
			resetFaqForm();
			updateFaq.resetState();
		}
	}, [createFaq.data, deleteFaq.success, updateFaq.data]);

	useEffect(() => {
		getAllFaqs.fetch();
	}, [category]);

	return (
		<div className={styles.main_home_container}>
			<Navbar className={styles.navDivider} />

			<main className={styles.mainContent}>
				<SuccessToast
					isVisible={successToast.isVisible}
					message={successToast.message}
					type={successToast.type}
					onClose={() => setSuccessToast((prev) => ({ ...prev, isVisible: false }))}
				/>

				<Loader
					visible={
						getStats.loading ||
						getInactiveUsers.loading ||
						getInactiveSignallers.loading ||
						getActiveSignallers.loading ||
						signallersToBePaid.loading ||
						signallersToBeRewarded.loading ||
						createCar.loading ||
						carCatalog.loading ||
						getCar.loading ||
						updateCar.loading ||
						deleteCar.loading ||
						createCarImage.loading ||
						deleteCarImage.loading ||
						createFaq.loading ||
						getAllFaqs.loading ||
						updateFaq.loading ||
						deleteFaq.loading ||
						searchUsers.loading
					}
				/>

				<div className={styles.mainCatalogContainer}>
					<div className={styles.section_container}>
						<h1 className={styles.title}>{`${t('adminPage.welcome')} ${authCtx.username}!`}</h1>
					</div>

					{/* STATISTICHE */}
					<div className={styles.section_container}>
						<p className={styles.section_title}>{t('adminPage.statsSection.title')}</p>
						<div className={styles.stats_grid}>
							<div className={styles.stats_column}>
								<div className={styles.stat_nr_click} onClick={() => setShowInactiveUsersModal(true)}>
									<p>{t('adminPage.statsSection.notActiveUsers')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totInactiveUsers || '0'}</div>
								</div>
								<div className={styles.stat_nr_click} onClick={() => setShowInactiveSignallersModal(true)}>
									<p>{t('adminPage.statsSection.inactiveSignallers')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totInactiveSignallers || '0'}</div>
								</div>
							</div>
							<div className={styles.stats_column}>
								<div className={styles.stat_nr_click} onClick={() => setShowActiveSignallersModal(true)}>
									<p>{t('adminPage.statsSection.totSignaller')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totSignaller || '0'}</div>
								</div>
								<div>
									<p>{t('adminPage.statsSection.totCars')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totCars || '0'}</div>
								</div>
							</div>
							<div className={styles.stats_column}>
								<div>
									<p>{t('adminPage.statsSection.totPayments')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totPayments || '0'}</div>
								</div>
								<div>
									<p>{t('adminPage.statsSection.totFaq')}</p>
									<div className={styles.stat_nr}>{getStats.data?.totFaqs || '0'}</div>
								</div>
							</div>
							<div className={styles.stats_column}>
								<div>
									<p>{t('adminPage.statsSection.signallersToBePaid')}</p>
									<div className={styles.stat_nr}>{signallersToBePaid.data?.length || '0'}</div>
								</div>
								<div>
									<p>{t('adminPage.statsSection.signallersToBeRewarded')}</p>
									<div className={styles.stat_nr}>{signallersToBeRewarded.data?.length || '0'}</div>
								</div>
							</div>
						</div>
					</div>

					{/* USER SEARCH */}
					<div className={styles.section_container}>
						<p className={styles.section_title}>{t('adminPage.search.title')}</p>

						<div className={styles.searchContainer}>
							<div className={styles.searchInputWrapper}>
								<input
									type="text"
									className={styles.searchInput}
									placeholder={t('adminPage.search.placeholder')}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
							</div>

							{searchTerm.length > 0 && (
								<div className={styles.searchResultsList}>
									{searchUsers.loading ? (
										<p>Loading...</p>
									) : searchUsers.data.length > 0 ? (
										searchUsers.data.map((item, index) => {
											if (!item.user) return null;
											const status = getStatusDetails(item.statusType);
											return (
												<div key={item.user.id || index} className={styles.searchResultItem}>
													<span className={styles.userEmail}>{item.user.email}</span>
													<span className={`${styles.userStatus} ${status.className}`}>{status.text}</span>
												</div>
											);
										})
									) : (
										<p>{t('adminPage.search.noResults')}</p>
									)}
								</div>
							)}

							{searchUsers.data.length > 0 && <Pagination contextId={SEARCH_USERS_CONTEXT_ID} />}
						</div>
					</div>

					{/* GESTIONE OFFERTE */}
					<div className={styles.section_container}>
						<p className={styles.section_title}>{t('adminPage.offersSection.title')}</p>

						{/* Crea Offerta */}
						<Collapse
							title={isEditingCar ? t('adminPage.offersSection.updateOffer') : t('adminPage.offersSection.createOffer')}
							collapsed={isEditingCar}
							id={'carForm'}
						>
							<CarForm
								onSubmit={handleCarSubmit}
								editingCar={carCatalog.data.find((car) => car.id === editingCarId) || null}
								isEditing={isEditingCar}
								existingImages={existingCarImages}
								onCancelEdit={() => {
									// Resetta gli stati di modifica
									setEditingCarId(null);
									setIsEditingCar(false);
									setExistingCarImages([]);
									setOriginalImageIds([]);
								}}
							/>
						</Collapse>

						{/* Modifica Offerta */}
						<div className={styles.mainCatalogContainer}>
							<div className={styles.catalog_header}>
								<Label text={t('adminPage.offersSection.editOffer')} className={styles.catalog_title} />
							</div>
							<div className={styles.car_category_container}>
								<button
									type={'button'}
									onClick={() => setCarCatalogCategory('RENTAL')}
									name={t('catalog.filters.rental')}
									className={`${styles.faq_categoria_btn} ${carCatalogCategory === 'RENTAL' ? styles.selected : ''}`}
								>
									{t('catalog.filters.rental').toUpperCase()}
								</button>
								<button
									type={'button'}
									onClick={() => setCarCatalogCategory('USED')}
									name={t('catalog.filters.used')}
									className={`${styles.faq_categoria_btn} ${carCatalogCategory === 'USED' ? styles.selected : ''}`}
								>
									{t('catalog.filters.used').toUpperCase()}
								</button>
								{/* 
								<button
									type={'button'}
									onClick={() => setCarCatalogCategory('VARIOUS')}
									name={t('catalog.filters.various')}
									className={`${styles.faq_categoria_btn} ${carCatalogCategory === 'VARIOUS' ? styles.selected : ''}`}
								>
									{t('catalog.filters.various').toUpperCase()}
								</button>
								*/}
							</div>
							<div className={styles.gridContainer}>
								{carCatalog.data.map((car) => (
									<CarCard
										key={car.id}
										carData={car}
										firstImageId={car.imageIds && car.imageIds.length > 0 ? car.imageIds[0] : null}
										onEdit={() => handleEditCar(car)}
										onDisable={handleDisableCar}
									/>
								))}
								{carCatalog.data.length === 0 && <div>{t('adminPage.offersSection.noCarsAvailable')}</div>}
							</div>
							{/* Componente di paginazione */}
							<Pagination contextId={ADMIN_CARS_CONTEXT_ID} />
						</div>
					</div>

					{/* GESTIONE SEGNALATORI */}
					<div className={styles.section_container}>
						<p className={styles.section_title}>{t('adminPage.signaller.title')}</p>
						<div className={styles.signallerGrid}>
							{/* Segnalatori da pagare: Contratti */}
							<SignallerList
								data={signallersToBePaid.data}
								title={t('adminPage.signaller.signallerList')}
								payFunction="pay"
								onPaymentSuccess={() => {
									signallersToBePaid.fetch(); // Aggiorna lista
									signallersToBeRewarded.fetch(); // Aggiorna lista
									getStats.fetch(); // Aggiorna statistiche
								}}
							/>

							{/* Segnalatori da pagare: Premi */}
							<SignallerList
								data={signallersToBeRewarded.data}
								title={t('adminPage.signaller.signallerRewardList')}
								payFunction="reward"
								onPaymentSuccess={() => {
									signallersToBeRewarded.fetch(); // Aggiorna lista
									getStats.fetch(); // Aggiorna statistiche
								}}
							/>
						</div>
					</div>

					{/* GESTIONE FAQ */}
					<div className={styles.section_container}>
						<p className={styles.section_title}>{t('adminPage.faqSection.title')}</p>
						<div className={styles.mainFaqContainer}>
							{/* Crea faq */}
							<div className={styles.faq_header}>
								<Label text={t('adminPage.faqSection.createFaq')} className={styles.faq_title} />
							</div>
							<form
								id={isEditingFaq ? 'updateFaqForm' : 'createFaqForm'}
								className={styles.single_faq_container}
								onSubmit={isEditingFaq ? updateFaqSubmit : createFaqSubmit}
							>
								<div className={styles.question_container}>
									<textarea
										rows={2}
										value={createQuestion}
										className={styles.question}
										placeholder={t('adminPage.faqSection.questionPlaceholder')}
										onChange={(e) => {
											setCreateQuestion(e.target.value);
										}}
									/>
								</div>

								<div className={styles.risposta_container}>
									<hr></hr>
									<div className={styles.faq_category_container}>
										<button
											type={'button'}
											onClick={() => {
												setCreateCategory('MINIMUM_CUSTOMER_REQUIREMENTS');
											}}
											name={t('faq.category.cat1')}
											className={`${styles.faq_categoria_btn} ${
												createCategory === 'MINIMUM_CUSTOMER_REQUIREMENTS' ? styles.selected : ''
											}`}
										>
											{t('faq.category.cat1')}
										</button>
										<button
											type={'button'}
											onClick={() => setCreateCategory('GENERAL_CONDITIONS')}
											name={t('faq.category.cat2')}
											className={`${styles.faq_categoria_btn} ${createCategory === 'GENERAL_CONDITIONS' ? styles.selected : ''}`}
										>
											{t('faq.category.cat2')}
										</button>
										<button
											type={'button'}
											onClick={() => setCreateCategory('PAYMENT_RULES')}
											name={t('faq.category.cat3')}
											className={`${styles.faq_categoria_btn} ${createCategory === 'PAYMENT_RULES' ? styles.selected : ''}`}
										>
											{t('faq.category.cat3')}
										</button>
										<button
											type={'button'}
											onClick={() => setCreateCategory('MISCELLANEOUS')}
											name={t('faq.category.cat4')}
											className={`${styles.faq_categoria_btn} ${createCategory === 'MISCELLANEOUS' ? styles.selected : ''}`}
										>
											{t('faq.category.cat4')}
										</button>
									</div>

									<hr></hr>
									<textarea
										rows={4}
										value={createAnswer}
										className={styles.answer}
										placeholder={t('adminPage.faqSection.answerPlaceholder')}
										onChange={(e) => {
											setCreateAnswer(e.target.value);
										}}
									/>
								</div>

								<div className={styles.faq_box_btn}>
									<div className={styles.faq_btns}>
										<Button
											className={styles.faq_btn}
											typeSubmit="submit"
											onClick={() => {}}
											name={isEditingFaq ? t('adminPage.faqSection.editBtn') : t('adminPage.faqSection.createBtn')}
											color="black"
											background="white"
										/>
										<Button
											className={styles.faq_btn}
											typeSubmit="button"
											onClick={() => resetFaqForm()}
											name={t('adminPage.faqSection.cancelBtn')}
											color="black"
											background="white"
										/>
									</div>
								</div>
							</form>

							{/* Modifica faq */}
							<div className={styles.faq_header}>
								<Label text={t('adminPage.faqSection.editFaq')} className={styles.faq_title} />
							</div>
							<div className={styles.faq_category_container}>
								<button
									type={'button'}
									onClick={() => {
										changeCategory('MINIMUM_CUSTOMER_REQUIREMENTS');
									}}
									name={t('faq.category.cat1')}
									className={`${styles.faq_categoria_btn} ${
										category === 'MINIMUM_CUSTOMER_REQUIREMENTS' ? styles.selected : ''
									}`}
								>
									{t('faq.category.cat1')}
								</button>
								<button
									type={'button'}
									onClick={() => {
										changeCategory('GENERAL_CONDITIONS');
									}}
									name={t('faq.category.cat2')}
									className={`${styles.faq_categoria_btn} ${category === 'GENERAL_CONDITIONS' ? styles.selected : ''}`}
								>
									{t('faq.category.cat2')}
								</button>
								<button
									type={'button'}
									onClick={() => {
										changeCategory('PAYMENT_RULES');
									}}
									name={t('faq.category.cat3')}
									className={`${styles.faq_categoria_btn} ${category === 'PAYMENT_RULES' ? styles.selected : ''}`}
								>
									{t('faq.category.cat3')}
								</button>
								<button
									type={'button'}
									onClick={() => {
										changeCategory('MISCELLANEOUS');
									}}
									name={t('faq.category.cat4')}
									className={`${styles.faq_categoria_btn} ${category === 'MISCELLANEOUS' ? styles.selected : ''}`}
								>
									{t('faq.category.cat4')}
								</button>
							</div>
							<div className={styles.single_faq_main_container}>
								{getAllFaqs.data?.map((faq: FAQDTO) => (
									<Faq
										key={faq.id}
										id={faq.id}
										question={faq.question || ''}
										answer={faq.answer || ''}
										category={faq.category || ''}
										updatable={isRoleAdmin ? true : false}
										onDelete={(id) => handleDeleteFaq(id)}
										onEdit={(faq) => handleEditFaq(faq)}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</main>

			<Modal isOpen={showInactiveUsersModal} onClose={() => setShowInactiveUsersModal(false)}>
				<InactiveUsersList userList={getInactiveUsers.data} paginationContext={ADMIN_INACTIVE_USERS} dataType="user" />
			</Modal>

			<Modal isOpen={showInactiveSignallersModal} onClose={() => setShowInactiveSignallersModal(false)}>
				<InactiveUsersList
					userList={getInactiveSignallers.data}
					paginationContext={ADMIN_INACTIVE_SIGNALLERS}
					dataType="signaller"
				/>
			</Modal>

			<Modal isOpen={showActiveSignallersModal} onClose={() => setShowActiveSignallersModal(false)}>
				<InactiveUsersList
					userList={getActiveSignallers.data}
					paginationContext={ADMIN_ACTIVE_SIGNALLERS}
					dataType="signaller-a"
				/>
			</Modal>

			<Footer />
		</div>
	);
};

export default Admin;
